import { useCallback, useId } from 'react';
import { useFocusEffect } from 'expo-router';
import {
  RightSidebarTab,
  useRightSidebar,
} from '~/layout/contexts/right-sidebar-layout/RightSidebarLayout';
import { RightSidebarRail } from '~/layout/components/right-sidebar/RightSidebarRail';
import { RightSidebarOutlet } from '~/layout/components/right-sidebar/RightSidebarOutlet';
import { RightSidebarPanelHeader } from '~/layout/components/right-sidebar/RightSidebarPanelHeader';
import { RightSidebarDrawer } from '~/layout/components/right-sidebar/RightSidebarDrawer';
import { RightSidebarTrigger } from '~/layout/components/right-sidebar/RightSidebarTrigger';

export interface RightSidebarProps {
  /** Ordered rail tabs. Memoize at the call site so registration is stable. */
  tabs: RightSidebarTab[];
  /** Panel width when a tab is open; defaults to the config's `open`. */
  expandedWidth?: number;
}

/**
 * Declarative slot a route renders to inject its right-sidebar rail, e.g.
 * `<RightSidebar tabs={tabs} expandedWidth={340} />`. It registers the tabs with
 * the layout (which renders the icon rail + active panel) and renders nothing
 * itself. Symmetric with how each route group declares its header.
 *
 * Registration is tied to screen *focus*, not mount: the `(sidebar)` group is a
 * Stack navigator that keeps backgrounded screens mounted, so a mount effect's
 * cleanup would never run when navigating away and the rail would stay docked on
 * screens that register none. `useFocusEffect` unregisters on blur and
 * re-registers on return.
 */
const RightSidebarRoot = ({ tabs, expandedWidth }: RightSidebarProps) => {
  const id = useId();
  const { registerRail, unregisterRail } = useRightSidebar();

  useFocusEffect(
    useCallback(() => {
      registerRail(id, tabs, { expandedWidth });
      return () => unregisterRail(id);
    }, [id, tabs, expandedWidth, registerRail, unregisterRail]),
  );

  return null;
};

/**
 * Right-sidebar API, exposed shadcn-style as a compound component:
 *  - `<RightSidebar tabs={…} />` — the registration slot (the root).
 *  - `RightSidebar.Rail` / `RightSidebar.Outlet` — desktop icon rail + active panel
 *    (rendered by `SidebarLayout`).
 *  - `RightSidebar.PanelHeader` — shared panel header (icon / title / actions).
 *  - `RightSidebar.Drawer` — the mobile drawer that hosts the registered rail.
 *  - `RightSidebar.Trigger` — the generic ellipsis button that opens the drawer.
 */
export const RightSidebar = Object.assign(RightSidebarRoot, {
  Rail: RightSidebarRail,
  Outlet: RightSidebarOutlet,
  PanelHeader: RightSidebarPanelHeader,
  Drawer: RightSidebarDrawer,
  Trigger: RightSidebarTrigger,
});
